import { unified } from 'unified';
import remarkParse from 'remark-parse';
import { toMarkdown } from 'mdast-util-to-markdown';
import type { RootContent } from 'mdast';

export type ContentBlock = {
    type: 'liveTsx' | 'markdown' | 'slot';
    content?: string;
    code?: string;
    /** For `slot` blocks: the SlotId from a UI tool's result, matched to a
     *  `{{Component:<slotId>}}` token in the assistant text. */
    slotId?: string;
    /**
     * Per-block lifecycle state. False once a closed liveTsx fence has
     * been parsed out of the chat text (the sidecar's build succeeded
     * and the compiled JS is safe to mount). True for a "loading"
     * marker without a paired closing fence, and for the trailing
     * unclosed fence at the end of streaming text.
     */
    partial?: boolean;
    /**
     * 0..1 progress hint for partial liveTsx blocks. Derived from the
     * count of <i data-lt-tick></i> markers the server emits into the
     * chat text every `STRIDE` chars of TSX body received (see
     * `LiveTsxProgressStride` in JsxLiveStreamProcessor.cs). Mapped to
     * `MAX_CHARS_FOR_FULL` and clamped to 1. Consumed by
     * `GeneratingVisualization` to fill the placeholder's bar buckets.
     */
    progress?: number;
    bytes?: number
};

// Languages we treat as live-renderable JSX. The canonical tag is
// `livetsx`, but the agent occasionally emits `tsx` / `jsx` (the more
// common ecosystem labels), and a model that's been instructed in
// either casing should still get live rendering. All blocks the agent
// produces in our domain are meant to render — we don't ship static
// JSX documentation in chat — so widening the set is safe.
const JSX_LANGS = new Set(['livetsx', 'tsx', 'jsx']);

// Invisible HTML sentinel the server's JsxLiveStreamProcessor injects
// into the chat text the moment a liveTsx fence opens on the LLM side.
// The div has display:none so it occupies no layout and is preserved
// through session persistence/replay. While the fence is being written
// the div sits alone in the text (no fence yet) and parseBlocks emits a
// placeholder block in its position; when the closed fence eventually
// arrives in the same stream, parseBlocks pairs them and the fence
// supersedes the div. Kept in sync with JsxLiveStreamProcessor.cs.
const LOADING_DIV_RE = /<div\b[^>]*\bdata-envision-livetsx="loading"[^>]*><\/div>/i;
const LOADING_DIV_RE_G = /<div\b[^>]*\bdata-envision-livetsx="loading"[^>]*><\/div>/gi;

// Progress markers the server interleaves into the chat text during
// fence writing — one <i data-lt-tick></i> per `STRIDE` chars of TSX
// body received. Kept in sync with `LiveTsxProgressStride` /
// `LiveTsxTickMarker` in JsxLiveStreamProcessor.cs.
const TICK_RE_G = /<i data-lt-tick><\/i>/g;
// Default "100%" cap — the rough char count of a typical liveTsx body.
// Surfaces whose snippets tend to be larger (or smaller) can override
// via `LiveTsxConfigProvider` / the `maxCharsForFull` option below;
// this is just the fallback used when no provider is in scope.
export const DEFAULT_MAX_CHARS_FOR_FULL = 2000;
const STRIDE = 100;
// Matches a closed liveTsx fence anywhere in a substring (multi-line
// body). Used to decide whether the trailing loading-div still has
// progress to surface or has already been resolved by a closed fence.
const CLOSED_FENCE_RE = /```(?:liveTsx|tsx|jsx)\s*\n[\s\S]*?\n```/i;

/**
 * Walks the raw chat text and returns a 0..1 progress value for the
 * trailing in-flight liveTsx block, if any. The trailing block is
 * defined as the last loading-div that has NO closed fence after it.
 * Tick markers between that div and end-of-text are the progress.
 *
 * `maxCharsForFull` is the surface-specific cap that maps tick count
 * to a fraction — pass a larger value when typical fences run long.
 */
function computeProgress(rawText: string, maxCharsForFull: number): [number,number] {
    let lastDivEnd = -1;
    let m: RegExpExecArray | null;
    // Local copy of the global regex so we don't share `lastIndex` state.
    const re = new RegExp(LOADING_DIV_RE_G.source, LOADING_DIV_RE_G.flags);
    while ((m = re.exec(rawText)) !== null) {
        lastDivEnd = m.index + m[0].length;
    }
    if (lastDivEnd === -1) return [0,0];

    const after = rawText.slice(lastDivEnd);
    // Already-resolved block: ticks before its closed fence belong to
    // that fence and are about to be discarded by the AST walk anyway.
    if (CLOSED_FENCE_RE.test(after)) return [0,0];

    const ticks = after.match(TICK_RE_G);
    const count = ticks ? ticks.length : 0;
    const maxTicks = Math.max(1, maxCharsForFull / STRIDE);
    return [Math.min(count / maxTicks, 1), count];
}

/**
 * Detects an unclosed ```liveTsx fence at the end of streaming text.
 * Returns the text before the fence and the partial code inside it,
 * or null if no unclosed fence is found.
 */
function extractTrailingUnclosedFence(text: string): { before: string; partialCode: string } | null {
    const fenceRe = /^```(\w+)\s*$/gm;
    let lastFenceMatch: RegExpExecArray | null = null;
    let match: RegExpExecArray | null;

    while ((match = fenceRe.exec(text)) !== null) {
        if (JSX_LANGS.has(match[1].toLowerCase())) {
            lastFenceMatch = match;
        }
    }

    if (!lastFenceMatch) return null;

    const afterFence = text.slice(lastFenceMatch.index + lastFenceMatch[0].length);
    const hasClosing = /^```\s*$/m.test(afterFence);
    if (hasClosing) return null;

    const before = text.slice(0, lastFenceMatch.index);
    const partialCode = afterFence.replace(/^\n/, '');
    return { before, partialCode };
}

// A standalone `{{Component:<slotId>}}` paragraph is a placement marker for a
// UI tool component (the SlotId is generated server-side and returned in the
// tool result); LlmResponseRender splices the matching component here.
const SLOT_RE = /^\{\{\s*Component:\s*([\w-]+)\s*\}\}$/;

// Plain text of a paragraph whose children are all text nodes (so a wrapped or
// emphasised token isn't mistaken for a marker), trimmed; else null.
function paragraphText(node: RootContent): string | null {
    if (node.type !== 'paragraph') return null;
    const kids = (node as { children?: { type: string; value?: string }[] }).children ?? [];
    if (kids.length === 0 || !kids.every((c) => c.type === 'text')) return null;
    return kids.map((c) => c.value ?? '').join('').trim();
}

/**
 * Splits a markdown string into sequential content blocks:
 *  - 'markdown' — regular content (headings, paragraphs, non-JSX code fences, etc.)
 *  - 'liveTsx'  — fenced code blocks tagged ```liveTsx
 *
 * When the text ends with an unclosed ```liveTsx fence (common during streaming),
 * the partial code is still emitted as a 'liveTsx' block so it goes through
 * LiveJsxBlock (and skeleton detection) instead of the markdown renderer.
 */
export function parseContentBlocks(
    text: string,
    options?: { maxCharsForFull?: number },
): ContentBlock[] {
    if (!text?.trim()) return [];

    // Compute progress BEFORE stripping ticks (computeProgress relies on
    // them being present to count). After this, ticks are excised from
    // `text` so they never reach the markdown renderer or pollute the
    // partial-fence detector below.
    const [progress,count] = computeProgress(
        text,
        options?.maxCharsForFull ?? DEFAULT_MAX_CHARS_FOR_FULL,
    );
    text = text.replace(TICK_RE_G, '');

    let trailingPartialCode: string | null = null;
    const unclosed = extractTrailingUnclosedFence(text);
    if (unclosed) {
        text = unclosed.before;
        trailingPartialCode = unclosed.partialCode;
    }

    const ast = unified().use(remarkParse).parse(text);

    const blocks: ContentBlock[] = [];
    let buffer: RootContent[] = [];
    // True once a server-emitted loading div has been seen and no closing
    // fence has yet consumed it. The next liveTsx fence collapses both
    // into a single real block; if the stream ends with this flag still
    // set, a placeholder block is emitted at end-of-AST.
    let pendingLoading = false;

    const flushBuffer = () => {
        if (buffer.length === 0) return;
        const md = toMarkdown({ type: 'root', children: buffer } as any);
        if (md.trim()) {
            blocks.push({ type: 'markdown', content: md });
        }
        buffer = [];
    };

    for (const node of ast.children) {
        // Loading sentinel — invisible signal that a fence has opened on
        // the server side. We keep the flag set even across additional
        // loading divs (e.g. a retry after an ESLint failure that
        // re-emits its own loading sentinel) so the eventual closed
        // fence still collapses everything into a single block instead
        // of emitting orphaned placeholders for stale attempts.
        if (node.type === 'html' && LOADING_DIV_RE.test(node.value)) {
            flushBuffer();
            pendingLoading = true;
            continue;
        }

        // ```tsx / ```jsx fences are NOT live-rendered — the liveTsx runtime-eval
        // path is retired in favour of the present_tsx tool call. They fall through
        // to the markdown buffer below and render as ordinary code blocks.

        // `{{Component:<slotId>}}` on its own line → a placement slot for a UI
        // tool component, spliced in by LlmResponseRender at this position.
        const slotText = paragraphText(node);
        if (slotText) {
            const sm = slotText.match(SLOT_RE);
            if (sm) {
                flushBuffer();
                blocks.push({ type: 'slot', slotId: sm[1] });
                continue;
            }
        }

        buffer.push(node);
    }

    flushBuffer();

    // Loading sentinel emitted but no closing fence arrived before the
    // stream ended — the LLM is still writing the fence, or the session
    // was persisted mid-write. Surface a placeholder so LiveJsxBlock
    // shows the "Gerando visualização" card at the right position.
    if (pendingLoading) {
        blocks.push({ type: 'liveTsx', code: '', partial: true, progress, bytes:count*100 });
    }

    if (trailingPartialCode !== null) {
        blocks.push({ type: 'liveTsx', code: trailingPartialCode, partial: true });
    }

    return blocks;
}
